// member-portal/src/components/OrganizationForm.js — FULL FILE (syntax fixed)
// Referral Code management for SuperAdmin: buttons + modal
// Changes saved only when clicking "Update"
// All previous functionality preserved

import React, { useState, useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import './OrganizationForm.css';
import axios from 'axios';

const OrganizationForm = ({ user, goBack, org }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    enterpriseName: '',
    registrationNumber: '',
    vatNumber: '',
    contactNumber: '',
    countryCode: '+27',
    whatsappNumber: '',
    emailAccounts: '',
    emailNotifications: '',
    businessAddress: '',
    companyLogo: '',
    companyLogoFile: null,
    menu: '',
    menuFile: null,
    location: { lat: '', lng: '' },
  });
  const [referralCode, setReferralCode] = useState('None');
  const [promoters, setPromoters] = useState([]);
  const [selectedPromoterCode, setSelectedPromoterCode] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [isAddressFocused, setIsAddressFocused] = useState(false);
  const addressInputRef = useRef(null);

  const [promoterName, setPromoterName] = useState('');

  useEffect(() => {
    if (org) {
      const updatedFormData = {
        companyName: org.companyName || '',
        enterpriseName: org.enterpriseName || '',
        registrationNumber: org.registrationNumber || '',
        vatNumber: org.vatNumber || '',
        contactNumber: org.contactNumber || '',
        countryCode: org.countryCode || '+27',
        whatsappNumber: org.whatsappNumber || '',
        emailAccounts: org.emailAccounts || '',
        emailNotifications: org.emailNotifications || '',
        businessAddress: org.businessAddress || '',
        companyLogo: org.companyLogo || '',
        companyLogoFile: null,
        menu: org.menu || '',
        menuFile: null,
        location: org.location || { lat: -26.2041, lng: 28.0473 },
      };
      console.log('[OrgForm] Initialized formData from prop:', updatedFormData);
      setFormData(updatedFormData);

      const rc = org.referralCode || 'None';
      console.log('[OrgForm] Loaded referralCode from org:', rc);
      setReferralCode(rc);

      if (rc !== 'None') {
        fetchPromoterByReferralCode(rc);
      }
    } else if (user && user.token) {
      const fetchOrganization = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/organization`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          });
          if (!response.ok) throw new Error('Failed to fetch organization');
          const data = await response.json();
          const updatedFormData = {
            companyName: data.companyName || '',
            enterpriseName: data.enterpriseName || '',
            registrationNumber: data.registrationNumber || '',
            vatNumber: data.vatNumber || '',
            contactNumber: data.contactNumber || '',
            countryCode: data.countryCode || '+27',
            whatsappNumber: data.whatsappNumber || '',
            emailAccounts: data.emailAccounts || '',
            emailNotifications: data.emailNotifications || '',
            businessAddress: data.businessAddress || '',
            companyLogo: data.companyLogo || '',
            companyLogoFile: null,
            menu: data.menu || '',
            menuFile: null,
            location: data.location || { lat: -26.2041, lng: 28.0473 },
          };
          console.log('[OrgForm] Fetched organization data:', data);
          console.log('[OrgForm] Initialized formData from API:', updatedFormData);
          setFormData(updatedFormData);

          const rc = data.referralCode || 'None';
          console.log('[OrgForm] Loaded referralCode from API:', rc);
          setReferralCode(rc);

          if (rc !== 'None') {
            fetchPromoterByReferralCode(rc);
          }
        } catch (err) {
          console.error('[OrgForm] Error fetching organization:', err);
          setError(err.message);
        }
      };
      fetchOrganization();
    }
  }, [user, org]);

  useEffect(() => {
    if (user?.role === 'SuperAdmin') {
      const fetchPromoters = async () => {
        try {
          const token = localStorage.getItem('memberToken');
          const res = await fetch(`${process.env.REACT_APP_API_URL}/api/members/promoters`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            const errData = await res.json();
            throw new Error(errData.error || 'Failed to load promoters');
          }
          const data = await res.json();
          console.log('[OrgForm] Loaded promoters list:', data.length, 'promoters');
          setPromoters(data);
        } catch (err) {
          console.error('[OrgForm] Failed to load promoters:', err);
          setError('Failed to load promoter list for assignment');
        }
      };
      fetchPromoters();
    }
  }, [user?.role]);

  const fetchPromoterByReferralCode = async (code) => {
    try {
      console.log('[OrgForm] Fetching promoter for code:', code);
      const token = localStorage.getItem('memberToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/members/by-referral-code/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        console.warn('[OrgForm] Promoter lookup failed:', res.status);
        throw new Error('No promoter found');
      }

      const data = await res.json();
      console.log('[OrgForm] Promoter found:', data);

      const fullName = [data.name, data.surname].filter(Boolean).join(' ') || data.username || data.email || '';
      setPromoterName(fullName || 'Unknown Promoter');
    } catch (err) {
      console.error('[OrgForm] Failed to lookup promoter:', err);
      setPromoterName('');
    }
  };

  useEffect(() => {
    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places'],
    });

    loader
      .load()
      .then(() => setGoogleLoaded(true))
      .catch((err) => console.error('[OrgForm] Google Maps load failed:', err));
  }, []);

  useEffect(() => {
    if (googleLoaded && addressInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['establishment', 'geocode'],
        componentRestrictions: { country: 'ZA' },
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const formattedAddress = place.formatted_address || place.name || '';
          setFormData((prev) => ({
            ...prev,
            businessAddress: formattedAddress,
            location: { lat, lng },
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            location: { lat: -26.2041, lng: 28.0473 },
          }));
        }
      });
    }
  }, [googleLoaded]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'registrationNumber') {
      if (value && !/^\d{0,4}(\/\d{0,6}(\/\d{0,2})?)?$/.test(value)) {
        setError('Please use format YYYY/NNNNNN/NN (e.g., 2023/123456/07)');
      } else {
        setError('');
      }
    }
    if (name === 'contactNumber' || name === 'whatsappNumber') {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, '') }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'businessAddress' ? { location: { lat: -26.2041, lng: 28.0473 } } : {}),
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'registrationNumber' && value) {
      if (!/^\d{4}\/\d{6}\/\d{2}$/.test(value)) {
        setError('Registration number must be in format YYYY/NNNNNN/NN (e.g., 2023/123456/07)');
      } else {
        setError('');
      }
    }
  };

  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    if (file) {
      if (name === 'companyLogoFile') {
        const allowedTypes = ['image/jpeg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          setError('Only JPEG and PNG images are allowed for the company logo');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setError('Company logo file size must be less than 5MB');
          return;
        }
        setFormData((prev) => ({
          ...prev,
          companyLogoFile: file,
          companyLogo: '',
        }));
      } else if (name === 'menuFile') {
        if (file.type !== 'application/pdf') {
          setError('Only PDF files are allowed for the menu');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setError('Menu file size must be less than 5MB');
          return;
        }
        setFormData((prev) => ({
          ...prev,
          menuFile: file,
          menu: '',
        }));
      }
      setError('');
    }
  };

  const uploadFileToS3 = async (file, type) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/deals/presigned-url`,
        {
          fileType: file.type,
          fileName: file.name,
          type: type,
        },
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
      const { url, cloudfrontUrl } = response.data;

      await axios.put(url, file, {
        headers: { 'Content-Type': file.type },
      });

      return cloudfrontUrl;
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      throw new Error(`Failed to upload ${type}`);
    }
  };

  const handleClearReferral = () => {
    setReferralCode('None');
    setPromoterName('');
  };

  const handleOpenAssignModal = () => {
    setSelectedPromoterCode('');
    setShowAssignModal(true);
  };

  const handleAssignPromoter = () => {
    if (!selectedPromoterCode) {
      setError('Please select a promoter');
      return;
    }

    setReferralCode(selectedPromoterCode);
    fetchPromoterByReferralCode(selectedPromoterCode);
    setShowAssignModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (formData.registrationNumber && !/^\d{4}\/\d{6}\/\d{2}$/.test(formData.registrationNumber)) {
        throw new Error('Registration number must be in format YYYY/NNNNNN/NN (e.g., 2023/123456/07)');
      }

      let companyLogo = formData.companyLogo;
      let menu = formData.menu;

      if (formData.companyLogoFile) {
        companyLogo = await uploadFileToS3(formData.companyLogoFile, 'logo');
      }

      if (formData.menuFile) {
        menu = await uploadFileToS3(formData.menuFile, 'menu');
      }

      const payload = {
        orgId: org?._id,
        companyName: formData.companyName,
        enterpriseName: formData.enterpriseName,
        registrationNumber: formData.registrationNumber,
        vatNumber: formData.vatNumber,
        contactNumber: formData.contactNumber,
        countryCode: formData.countryCode,
        whatsappNumber: formData.whatsappNumber,
        emailAccounts: formData.emailAccounts,
        emailNotifications: formData.emailNotifications,
        businessAddress: formData.businessAddress,
        companyLogo,
        menu,
        location: formData.location,
        referralCode: isSuperAdmin ? (referralCode === 'None' ? null : referralCode.trim().toUpperCase()) : undefined,
      };

      console.log('[OrgForm] SUBMITTING PAYLOAD:', JSON.stringify(payload, null, 2));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/organization`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[OrgForm] UPDATE FAILED:', errorData);
        throw new Error(errorData.error || 'Failed to update organization');
      }

      const updatedOrg = await response.json();
      console.log('[OrgForm] UPDATE SUCCESS:', updatedOrg);

      setFormData((prev) => ({
        ...prev,
        companyLogo,
        menu,
        companyLogoFile: null,
        menuFile: null,
      }));

      const newRc = updatedOrg.referralCode || 'None';
      setReferralCode(newRc);
      if (newRc !== 'None') {
        fetchPromoterByReferralCode(newRc);
      } else {
        setPromoterName('');
      }

      goBack();
    } catch (err) {
      console.error('[OrgForm] SUBMIT ERROR:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  const mapLocation = formData.location.lat && formData.location.lng
    ? { lat: formData.location.lat, lng: formData.location.lng }
    : { lat: -26.2041, lng: 28.0473 };
  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${mapLocation.lat},${mapLocation.lng}&zoom=15`;

  if (loading) return <div className="loading">Processing...</div>;

  const isSuperAdmin = user?.role === 'SuperAdmin';
  const isAdministrator = user?.role === 'Administrator';
  const isEditable = isAdministrator || isSuperAdmin;

  if (!isEditable) {
    return (
      <div className="deal-form">
        <h2>Organization Details</h2>
        {error && <p className="error">{error}</p>}
        <div className="form-group">
          <label>Company Name</label>
          <p>{formData.companyName}</p>
        </div>
        <div className="form-group">
          <label>Enterprise Name</label>
          <p>{formData.enterpriseName || 'N/A'}</p>
        </div>
        <div className="form-group">
          <label>Registration Number</label>
          <p>{formData.registrationNumber || 'N/A'}</p>
        </div>
        <div className="form-group">
          <label>VAT Number</label>
          <p>{formData.vatNumber || 'N/A'}</p>
        </div>
        <div className="form-group">
          <label>Contact Number</label>
          <p>{`${formData.countryCode} ${formData.contactNumber}`}</p>
        </div>
        <div className="form-group">
          <label>WhatsApp Number</label>
          <p>{formData.whatsappNumber ? `${formData.countryCode} ${formData.whatsappNumber}` : 'N/A'}</p>
        </div>
        <div className="form-group">
          <label>Email - Accounts</label>
          <p>{formData.emailAccounts}</p>
        </div>
        <div className="form-group">
          <label>Email - Notifications</label>
          <p>{formData.emailNotifications}</p>
        </div>
        <div className="form-group">
          <label>Business Address</label>
          <div className="map-container">
            <iframe
              title="Business Address Location"
              className="map-iframe"
              src={mapUrl}
              allowFullScreen
              loading="lazy"
            />
          </div>
          <p>{formData.businessAddress}</p>
        </div>
        <div className="form-group">
          <label>Company Logo</label>
          {formData.companyLogo ? (
            <div className="logo-preview">
              <img
                src={formData.companyLogo}
                alt="Company Logo"
                className="company-logo-preview"
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
          ) : (
            <p>No logo available</p>
          )}
        </div>
        <div className="form-group">
          <label>Menu (PDF, Optional)</label>
          {formData.menu ? (
            <div className="menu-preview mb-2">
              <p>
                Menu: <a href={formData.menu} target="_blank" rel="noopener noreferrer" className="text-bright-blue underline">View PDF</a>
              </p>
            </div>
          ) : (
            <p>No menu available</p>
          )}
        </div>
        <div className="form-group">
          <label>Referral Code</label>
          <p>{referralCode}</p>
        </div>
        <div className="form-group">
          <label>Referred by Promoter</label>
          <p>{promoterName || ''}</p>
        </div>
        <div className="button-group">
          <button type="button" onClick={goBack} className="edit-button">
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="deal-form">
      <h2>Organization Details {isSuperAdmin ? '(SuperAdmin Edit)' : '(Administrator Edit)'}</h2>
      {error && <p className="error">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Company Name</label>
          <input
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            required
            placeholder="Enter company name"
          />
        </div>
        <div className="form-group">
          <label>Enterprise Name</label>
          <input
            name="enterpriseName"
            value={formData.enterpriseName}
            onChange={handleChange}
            required
            placeholder="Enter enterprise name"
          />
        </div>
        <div className="form-group">
          <label>Registration Number (YYYY/NNNNNN/NN)</label>
          <input
            name="registrationNumber"
            value={formData.registrationNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            placeholder="e.g., 2023/123456/07"
          />
        </div>
        <div className="form-group">
          <label>VAT Number</label>
          <input
            name="vatNumber"
            value={formData.vatNumber}
            onChange={handleChange}
            placeholder="Enter VAT number"
          />
        </div>
        <div className="form-group">
          <label>Contact Number</label>
          <div className="input-container">
            <select
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              className="country-code"
            >
              <option value="+27">+27</option>
              <option value="+44">+44</option>
              <option value="+1">+1</option>
            </select>
            <input
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              required
              placeholder="Enter contact number"
            />
          </div>
        </div>
        <div className="form-group">
          <label>WhatsApp Number</label>
          <div className="input-container">
            <select
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              className="country-code"
            >
              <option value="+27">+27</option>
              <option value="+44">+44</option>
              <option value="+1">+1</option>
            </select>
            <input
              name="whatsappNumber"
              value={formData.whatsappNumber}
              onChange={handleChange}
              placeholder="Enter WhatsApp number"
            />
          </div>
        </div>
        <div className="form-group">
          <label>Email - Accounts</label>
          <input
            name="emailAccounts"
            value={formData.emailAccounts}
            onChange={handleChange}
            required
            placeholder="Enter accounts email"
            type="email"
          />
        </div>
        <div className="form-group">
          <label>Email - Notifications</label>
          <input
            name="emailNotifications"
            value={formData.emailNotifications}
            onChange={handleChange}
            required
            placeholder="Enter notifications email"
            type="email"
          />
        </div>
        <div className="form-group relative">
          <label>Business Address</label>
          <div className="map-container">
            <iframe
              title="Business Address Location"
              className="map-iframe"
              src={mapUrl}
              allowFullScreen
              loading="lazy"
            />
          </div>
          <input
            ref={addressInputRef}
            name="businessAddress"
            value={formData.businessAddress}
            onChange={handleChange}
            onFocus={() => setIsAddressFocused(true)}
            onBlur={() => setIsAddressFocused(false)}
            required
            placeholder="Enter business address or name"
          />
          {isAddressFocused && (
            <div className="speech-bubble">
              Please select your business's Google Places listing if available, or choose your address from the Google Maps suggestions.
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Company Logo</label>
          {formData.companyLogo && (
            <div className="logo-preview">
              <img
                src={formData.companyLogo}
                alt="Company Logo"
                className="company-logo-preview"
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
          )}
          <input
            type="file"
            name="companyLogoFile"
            accept="image/jpeg,image/png"
            onChange={handleFileChange}
          />
        </div>
        <div className="form-group">
          <label>Menu (PDF, Optional)</label>
          {formData.menu && (
            <div className="menu-preview mb-2">
              <p>
                Current Menu: <a href={formData.menu} target="_blank" rel="noopener noreferrer">View PDF</a>
              </p>
            </div>
          )}
          <input
            type="file"
            name="menuFile"
            accept="application/pdf"
            onChange={handleFileChange}
          />
        </div>

        {/* Referral Code management — SuperAdmin gets buttons + modal */}
        <div className="form-group">
          <label>Referral Code</label>
          {isSuperAdmin ? (
            <div className="referral-controls">
              <div className="current-referral">
                Current: <strong>{referralCode === 'None' ? 'None assigned' : referralCode}</strong>
                {promoterName && <span> (Promoter: {promoterName})</span>}
              </div>

              <div className="referral-actions">
                <button
                  type="button"
                  className="clear-referral-btn"
                  onClick={handleClearReferral}
                  disabled={loading || referralCode === 'None'}
                >
                  Clear Referral
                </button>
                <button
                  type="button"
                  className="assign-promoter-btn"
                  onClick={handleOpenAssignModal}
                  disabled={loading}
                >
                  Assign Promoter
                </button>
              </div>
            </div>
          ) : (
            <p>{referralCode}</p>
          )}
        </div>

        <div className="form-group">
          <label>Referred by Promoter</label>
          <p>{promoterName || ''}</p>
        </div>

        <div className="button-group">
          <button type="submit" className="back-button" disabled={loading}>
            Update
          </button>
          <button type="button" onClick={goBack} className="edit-button" disabled={loading}>
            Cancel
          </button>
        </div>
      </form>

      {/* Assign Promoter Modal */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Assign a Promoter</h3>
            <p>Select a promoter to assign to this organization.</p>

            {promoters.length === 0 ? (
              <p>No active promoters found in the system.</p>
            ) : (
              <select
                value={selectedPromoterCode}
                onChange={(e) => setSelectedPromoterCode(e.target.value)}
                className="promoter-select"
              >
                <option value="">-- Select a Promoter --</option>
                {promoters.map((p) => (
                  <option key={p._id} value={p.referralCode}>
                    {p.name} {p.surname} ({p.referralCode})
                  </option>
                ))}
              </select>
            )}

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowAssignModal(false)}>
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleAssignPromoter}
                disabled={!selectedPromoterCode || loading}
              >
                Select
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationForm;